import { PRODUCTCATEGORIEEN } from './productcategorieen';
import { STAP_VALIDEREN, STAP_VASTLEGGEN } from './kerncijfers';
import type { StapKeuze } from './stapFilter';
import type { HandlingTimePerPersonRow } from './handlingTime.api';

/**
 * Story 34.1 — het zwaartepunt: in welke categorie het werk van iemand viel.
 *
 * Hier staat uitsluitend rekenwerk en geen opmaak, om dezelfde reden als bij `aandachtslijst.ts`.
 *
 * > [!note] Waarom dit naast de aandachtslijst staat en niet erin
 * > Het zwaartepunt verandert NIETS aan de rangschikking (AC-14). Die blijft op kosten sorteren; dit
 * > is uitleg en geen maatstaf. Zou het in `bouwAandachtslijst` zitten, dan was het een kwestie van
 * > tijd voordat iemand er een sortering of een filter op bouwde.
 *
 * > [!warning] Het totaal hier is HETZELFDE getal als de kolom `stuks` ernaast, en dat is de story
 * > Beide tellen ONDERSCHEIDEN PRODUCTEN binnen de stap (AC-0). De eerste versie telde hier
 * > afgeronde behandelingen — gemeten 221 tegen 81 producten, een factor bijna drie — en dan stond
 * > "Levensmiddelen, 31 van 35" naast een kolom die 13 zei. Beide getallen zien er plausibel uit, en
 * > dat is precies waarom `noemers.test.ts` de twee náást elkaar meet in plaats van elk voor zich.
 * >
 * > Het zwaartepunt draagt zijn eigen totaal mee en leest de kolom niet op: zo blijft de toets die
 * > de twee vergelijkt iets méten in plaats van een waarde met zichzelf te vergelijken.
 */

/**
 * Van de woorden op het scherm naar de woorden van de bron.
 *
 * Dit zijn HANDELINGSNAMEN (`processing`, `validation`) en geen procescodes, precies zoals
 * `BRONNAAM` in `stapFilter.ts`. Dezelfde naam als daar, zodat niemand hem "verbetert" naar
 * `processCode` — dan zou de kolom stil leeg vallen zonder dat er iets rood wordt.
 */
const BRONNAAM: Record<StapKeuze, string> = {
  vastleggen: STAP_VASTLEGGEN,
  valideren: STAP_VALIDEREN,
};

/** Het aantal decimalen waarop een grens beslecht wordt, precies zoals in `normen.ts`. */
const PRECISIE = 1e6;

/**
 * Afronden vóór een vergelijking, zodat drijvendekommaruis de grens niet verschuift.
 *
 * Bij hele aantallen verandert deze afronding niets, en dat is precies de bedoeling: de regel is
 * overgenomen van `contextkaarten.ts` zodat "wanneer is het hoogste gedeeld" op één manier beslecht
 * wordt in dit scherm. Zou een van de twee plekken ooit met een breuk gaan rekenen, dan doen ze dat
 * nog steeds op dezelfde manier (AC-13).
 */
function opPrecisie(waarde: number): number {
  return Math.round(waarde * PRECISIE) / PRECISIE;
}

export type ZwaartepuntSoort = 'categorie' | 'gedeeld' | 'geen' | 'onbekend';

/**
 * De VIER uitkomsten, als vier vormen en niet als één vorm met lege velden.
 *
 * - `categorie` — één categorie draagt het meeste werk.
 * - `gedeeld` — twee of meer delen het hoogste aantal (AC-13).
 * - `geen` — deze persoon werkte in deze stap aan geen enkel product.
 * - `onbekend` — de bron stuurt de uitsplitsing per categorie niet mee.
 *
 * Zo kan een lezer geen categorie tonen die er bij een gelijkspel niet is: het veld bestáát daar
 * niet.
 *
 * > [!warning] `geen` en `onbekend` zijn met opzet NIET dezelfde uitkomst
 * > Codereviewbevinding 31 augustus 2026. Ze vielen hier eerst samen, en dan staat er tijdens een
 * > uitrol "niets afgerond" naast élke medewerker terwijl er over niemand iets uitgesplitst is —
 * > een onwaarheid over iedereen tegelijk, precies de stille verdwijning die story 33.1 wegnam.
 *
 * `totaal` staat op alle vier, want ook zonder aanwijsbaar zwaartepunt is "hoeveel er dan wél
 * afgerond is" de zin die de kolom af maakt.
 */
export type Zwaartepunt =
  | {
    soort: 'categorie';
    /** `null` is *overige categorieën*. */
    code: string | null;
    /** Het aantal onderscheiden producten in díe categorie. */
    aantal: number;
    /** De onderscheiden producten van deze persoon in deze stap: de kolom Stuks ernaast. */
    totaal: number;
  }
  | { soort: 'gedeeld'; totaal: number }
  | { soort: 'geen'; totaal: number }
  | { soort: 'onbekend'; totaal: number };

const GEEN: Zwaartepunt = { soort: 'geen', totaal: 0 };
const ONBEKEND: Zwaartepunt = { soort: 'onbekend', totaal: 0 };

/** De volgorde van de story: de tien, dan *overige*. Bepaalt niets, maar houdt de uitkomst vast. */
const VOLGORDE: readonly (string | null)[] = [
  ...PRODUCTCATEGORIEEN.map((categorie) => categorie.code),
  null,
];

function positieVan(code: string | null): number {
  const index = VOLGORDE.indexOf(code);
  return index === -1 ? VOLGORDE.length : index;
}

/**
 * Het zwaartepunt van één persoon binnen één stap.
 *
 * GETELD BINNEN DE STAP waarop de lijst staat, net als de kolom ernaast — de aandachtslijst is per
 * stap, en een zwaartepunt over beide stappen zou naast een getal staan dat dat niet is (AC-12).
 *
 * BIJ EEN GEDEELD HOOGSTE WORDT ER NIETS AANGEWEZEN (AC-13). Twee, drie of alle categorieën gelijk
 * maakt geen verschil: de regel is `aantalHoogste > 1`, letterlijk dezelfde regel die de contextkaart
 * van story 33.3 draagt. Zou er tóch één aangewezen worden, dan besliste de volgorde van de lijst
 * wie er stond — en dat is willekeur met het uiterlijk van een bevinding.
 */
export function bepaalZwaartepunt(rij: HandlingTimePerPersonRow, stap: StapKeuze): Zwaartepunt {
  const bron = BRONNAAM[stap];
  const eigen = (rij.handlings ?? []).find((handling) => handling.handling === bron);
  const categorieen = eigen?.categories;
  // AFWEZIG IS IETS ANDERS DAN LEEG, en die twee krijgen hier dus een eigen uitkomst. Afwezig zegt
  // "deze bron kent de uitsplitsing nog niet", leeg zegt "deze persoon rondde niets af" — en het
  // scherm zegt allebei in eigen woorden. `eigen === undefined` telt als leeg: dan is er wél
  // uitgesplitst, maar deze persoon komt in deze stap niet voor.
  if (eigen !== undefined && categorieen === undefined) return ONBEKEND;
  if (categorieen === undefined || categorieen.length === 0) return GEEN;

  const regels = categorieen
    .filter((categorie) => categorie.tradeItems > 0)
    .map((categorie) => ({
      code: categorie.categoryCode,
      aantal: categorie.tradeItems,
    }))
    .sort((a, b) => (b.aantal - a.aantal) || (positieVan(a.code) - positieVan(b.code)));

  const totaal = regels.reduce((som, regel) => som + regel.aantal, 0);
  if (totaal === 0) return GEEN;

  const eerste = regels[0]!;
  const hoogste = opPrecisie(eerste.aantal);
  const aantalHoogste = regels.filter((regel) => opPrecisie(regel.aantal) === hoogste).length;

  if (aantalHoogste > 1) return { soort: 'gedeeld', totaal };
  return { soort: 'categorie', code: eerste.code, aantal: eerste.aantal, totaal };
}
